import { NavLink, Outlet } from 'react-router-dom';
import clsx from 'clsx';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { useAuth } from '../state/auth';

interface Stats {
  activeDownloads: number;
  monitoredWanted: number;
}

interface NavItem {
  to: string;
  label: string;
  end?: boolean;
  admin?: boolean;
}

const NAV: NavItem[] = [
  { to: '/', label: 'Library', end: true },
  { to: '/add', label: 'Add' },
  { to: '/search', label: 'Search' },
  { to: '/activity', label: 'Activity' },
  { to: '/history', label: 'History' },
  { to: '/library-sync', label: '🛰 Library Sync' },
  { to: '/users', label: 'Users', admin: true },
  { to: '/settings', label: 'Settings', admin: true },
];

export function Layout() {
  const { status, logout } = useAuth();
  const isAdmin = status?.role === 'admin';
  const { data: stats } = useQuery({
    queryKey: ['stats'],
    queryFn: () => api.get<Stats>('/api/stats'),
    refetchInterval: 10_000,
  });

  return (
    <div className="flex min-h-screen">
      <aside className="flex w-56 flex-col border-r border-slate-800 bg-slate-900/40 p-4">
        <div className="mb-6">
          <div className="text-base font-semibold text-indigo-300">jelly-dl</div>
          <div className="text-xs text-slate-500">Movies &amp; TV</div>
        </div>
        <nav className="flex-1 space-y-1">
          {NAV.filter((n) => !n.admin || isAdmin).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                clsx(
                  'flex items-center justify-between rounded-md px-3 py-2 text-sm',
                  isActive ? 'bg-indigo-600/20 text-indigo-200' : 'text-slate-300 hover:bg-slate-800',
                )
              }
            >
              <span>{n.label}</span>
              {n.to === '/activity' && stats && stats.activeDownloads > 0 && (
                <span className="rounded-full bg-blue-600 px-2 text-xs">{stats.activeDownloads}</span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="mt-4 border-t border-slate-800 pt-4 text-xs text-slate-500">
          <div className="mb-2">Signed in as {status?.username}</div>
          <button onClick={() => void logout()} className="text-slate-400 hover:text-slate-200">
            Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-6xl p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
